import { FormValues } from '../../components/form/types';
import { PaginationTypeResponse } from '../types';
import { LocaleResponse, TranslationValueResponse } from '../types/translation';
import { ApiFactoryClassType } from '../factory';

const translationAdmin = (factory: ApiFactoryClassType) => ({
    getLocales: async (
        limit: number = 15,
        offset: number = 0,
        sort?: string,
        order?: 'asc' | 'desc',
        onlyLang?: boolean,
    ): Promise<PaginationTypeResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/admin/locale/list?limit=${limit}&offset=${offset}${sort && order ? `&sort=${sort}&order=${order}` : ``}${onlyLang ? `&onlyLang=true` : ``}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            if (response.status >= 400) {
                return null;
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    createLocale: async (
        localeObj: FormValues,
    ): Promise<LocaleResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL('/api/admin/locale/create', factory.getApiHost()),
                {
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(localeObj),
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    editLocale: async (
        localeId: string,
        localeObj: FormValues,
    ): Promise<LocaleResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/admin/locale/${localeId}`, factory.getApiHost()),
                {
                    method: 'PUT',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(localeObj),
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    copyLocale: async (
        localeId: string,
        optionalParams?: { translateToLocale?: string },
    ): Promise<LocaleResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/admin/locale/${localeId}/copy`,
                    factory.getApiHost(),
                ),
                {
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(optionalParams),
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getTranslationsByLocale: async (
        localeId: string,
        limit: number = 15,
        offset: number = 0,
        sort?: string,
        order?: 'asc' | 'desc',
    ): Promise<PaginationTypeResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/admin/locale/${localeId}/translations?limit=${limit}&offset=${offset}${sort && order ? `&sort=${sort}&order=${order}` : ``}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            if (response.status >= 400) {
                return null;
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    createTranslation: async (
        localeId: string,
        translationObj: FormValues,
    ): Promise<TranslationValueResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/admin/locale/${localeId}/translations`,
                    factory.getApiHost(),
                ),
                {
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(translationObj),
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    editTranslation: async (
        translationId: string,
        translationObj: FormValues,
    ): Promise<TranslationValueResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/admin/locale/translation/${translationId}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'PUT',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(translationObj),
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
});

export default translationAdmin;
